import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, AuthState } from '@/types';

// Helper to check if access token cookie is expired using the companion cookie
function isSessionExpired(): boolean {
  // argos_access_exp is a non-httpOnly cookie containing the expiry timestamp
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'argos_access_exp') {
      const expTs = parseInt(value, 10);
      if (isNaN(expTs)) return true;
      // Expired if within 60 seconds of expiry
      return expTs * 1000 < Date.now() + 60000;
    }
  }
  // No expiry cookie means no valid session
  return true;
}

interface AuthStore extends AuthState {
  products: string[];
  allAccess: boolean;
  _hasHydrated: boolean;
  viewAsRole: string | null;
  setUser: (user: User | null) => void;
  setAccessToken: (token: string | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setProducts: (products: string[], allAccess?: boolean) => void;
  addProduct: (product: string) => void;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  checkAndClearExpiredSession: () => boolean;
  setHasHydrated: (state: boolean) => void;
  setViewAsRole: (role: string | null) => void;
  getEffectiveRole: () => string | undefined;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      products: [],
      allAccess: false,
      _hasHydrated: false,
      viewAsRole: null,

      setHasHydrated: (state) => set({ _hasHydrated: state }),

      setViewAsRole: (role) => set({ viewAsRole: role }),

      getEffectiveRole: () => {
        const state = get();
        if (state.user?.role === 'superadmin' && state.viewAsRole) {
          return state.viewAsRole;
        }
        return state.user?.role;
      },

      setUser: (user) => set({ user }),

      // Keep setAccessToken for backward compat but tokens are in cookies now
      setAccessToken: (_accessToken) => set({}),

      setTokens: (_accessToken, _refreshToken) => set({}),

      setProducts: (products, allAccess = false) =>
        set({ products, allAccess }),

      addProduct: (product) => {
        const currentProducts = get().products;
        if (!currentProducts.includes(product)) {
          set({ products: [...currentProducts, product] });
        }
      },

      login: (user, _accessToken, _refreshToken) =>
        set({
          user,
          accessToken: null,  // Tokens are in httpOnly cookies, not in store
          refreshToken: null,
          isAuthenticated: true,
          _hasHydrated: true,
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          products: [],
          allAccess: false,
          viewAsRole: null,
        }),

      // Check if session is expired using companion cookie
      checkAndClearExpiredSession: () => {
        const state = useAuthStore.getState();
        if (state.isAuthenticated && isSessionExpired()) {
          useAuthStore.getState().logout();
          return true; // Session was cleared
        }
        return false;
      },
    }),
    {
      name: 'argos-auth',
      partialize: (state) => ({
        // Only persist user info and product access — NOT tokens
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        products: state.products,
        allAccess: state.allAccess,
      }),
      onRehydrateStorage: () => {
        return (_hydratedState, error) => {
          setTimeout(() => {
            if (error) {
              console.error('[Auth] Rehydration error:', error);
              useAuthStore.setState({ _hasHydrated: true });
              return;
            }

            // Mark hydration complete — session validity is checked via cookie, not stored tokens
            useAuthStore.setState({ _hasHydrated: true });
          }, 0);
        };
      },
    }
  )
);
